import { Modal, Select, Input, Form, message } from "antd";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { UserInfo, FormField, CreateInquiryVM } from "../models/models";
import { createInquiry } from "../models/serverApi";

interface InquiryModalProps {
    open: boolean;
    onClose: () => void;
    formField: FormField;
    formId: number;
    sectionId: number;
    ecrfId: number;
    currentFieldValue?: string;
}

export const InquiryModal: React.FC<InquiryModalProps> = ({
    open, onClose, formField, formId, sectionId, ecrfId, currentFieldValue
}) => {
    const [context] = useContext(AppContext);
    const [form] = Form.useForm();
    const [users, setUsers] = useState<UserInfo[]>([]);
    const [submitting, setSubmitting] = useState(false);

    // Queries go to the helpdesk, not to whichever colleague the investigator
    // happened to pick out of a list of every user on the platform. Routing a
    // clinical query to another centre's investigator meant it could sit unread
    // indefinitely, and there was no single place to see what was outstanding.
    //
    // Filtered to admins rather than to a hardcoded address, so adding a second
    // helpdesk account is a role assignment rather than a code change.
    useEffect(() => {
        if (!open) return;
        fetch("/api/User/All", { headers: { Accept: "application/json" } })
            .then(r => r.json())
            .then((data: UserInfo[]) => {
                // Admins are NOT excluded from their own list. With a single
                // helpdesk account the admin is the only recipient, so filtering
                // themselves out left an empty dropdown and no way to raise a query
                // at all. Addressing a query to the helpdesk is addressing a role,
                // not a person - an admin noting something against a field is the
                // same act as an investigator doing it, and it belongs in the same
                // queue rather than nowhere.
                const helpdesk = data.filter(u => u.admin);
                setUsers(helpdesk);

                // With a single helpdesk account, asking the user to choose from a
                // list of one is friction with no decision in it.
                if (helpdesk.length === 1) {
                    form.setFieldsValue({ toUserId: helpdesk[0].id });
                }
            });
    }, [open]);

    const handleOk = () => {
        form.validateFields().then(async (values) => {
            setSubmitting(true);
            const vm: CreateInquiryVM = {
                toUserId: values.toUserId,
                fieldId: formField.id,
                formId: formId,
                sectionId: sectionId,
                ecrfId: ecrfId,
                patientId: context.selection?.patient?.id ?? 0,
                patientAdmissionId: context.selection?.admission?.id ?? 0,
                note: values.note,
                fieldValueSnapshot: currentFieldValue,
            };
            const result = await createInquiry(vm);
            setSubmitting(false);
            if (result) {
                message.success("Η ερώτηση στάλθηκε");
                form.resetFields();
                onClose();
            } else {
                message.error("Σφάλμα κατά την αποστολή της ερώτησης");
            }
        });
    };

    return (
        <Modal
            open={open}
            title={`Ερώτηση για πεδίο: "${formField.label}"`}
            okText="Αποστολή"
            cancelText="Ακύρωση"
            confirmLoading={submitting}
            onOk={handleOk}
            onCancel={() => { form.resetFields(); onClose(); }}
        >
            {currentFieldValue && (
                <p><strong>Τρέχουσα τιμή:</strong> {currentFieldValue}</p>
            )}
            <Form form={form} layout="vertical">
                <Form.Item
                    name="toUserId"
                    label="Αποστολή σε"
                    rules={[{ required: true, message: "Επιλέξτε παραλήπτη" }]}
                >
                    <Select
                        showSearch
                        placeholder="Helpdesk"
                        optionFilterProp="label"
                        // Named for the role, not the person. An investigator is
                        // raising a query with the study helpdesk; whose account
                        // answers it is an internal detail that should not change
                        // how the question is addressed.
                        options={users.map(u => ({
                            value: u.id,
                            label: `Helpdesk (${u.email})`
                        }))}
                    />
                </Form.Item>
                <Form.Item name="note" label="Σημείωση (προαιρετικά)">
                    <Input.TextArea rows={3} maxLength={2000} showCount />
                </Form.Item>
            </Form>
        </Modal>
    );
};
